/**
 * useFitWidthMeasure — COMPOSED 낱장 묶음을 컨테이너 폭에 **축소만** 해서 맞추는 fit 실측 훅.
 * `ComposedFitWidthFrame`(데스크탑 정적 프레임)과 `ComposedPinchViewport`(모바일 핀치/팬)가 같은
 * 실측 규약을 공유한다 — 두 컴포넌트에 effect를 복제하지 말 것.
 *
 * 규약:
 *  - fit scale = **실측한 컨테이너 폭** / 낱장 폭, 최대 1(넓으면 확대하지 않는다). 하한(legacy 50%)은
 *    두지 않는다 — 하한이 걸리면 컨테이너보다 넓어져 다시 잘린다.
 *  - 가용 폭은 **호출자가 소유한 노드**(`measureRef`)에서 잰다. window 폭 가정은 레이아웃마다 틀린다
 *    (2026-08-19 dev 실측: 390px 폰에서 가정 358px vs 실제 컨테이너 326px → 71px 잘림).
 *  - wrapper-shrink용 자연높이는 `innerRef` 노드의 offsetHeight — transform 비의존이라 scale과 서로를
 *    유발하는 피드백 루프가 없다. 바깥 박스의 명시 높이는 안쪽(고정폭, auto 높이) offsetHeight에 영향을
 *    주지 않는다.
 *  - 둘 다 **useLayoutEffect**다. 폭은 첫 페인트 전에 잡아 잘린 한 프레임을 없애고, 자연높이는
 *    useEffect로 두면 첫 프레임에 null이라 바깥 박스가 자연높이(축소 전, 예: 4564px)로 잡혀 축소본
 *    (1874px) 아래로 빈 pan 영역이 한 프레임 그려진다.
 *  - 리사이즈·회전은 ResizeObserver가 받는다(window 이벤트보다 정확 — 컨테이너 폭은 chrome 변화로도
 *    바뀐다).
 *
 * `enabled=false`면 어느 것도 관측하지 않는다(ResizeObserver 미생성·상태 미갱신) — PinchViewport의
 * 데스크탑 분기가 정적 프레임으로 떨어질 때 모바일 쪽 실측이 헛돌지 않게 하는 게이트다.
 * `remeasureKey`는 자연높이 effect의 재실행 키(PinchViewport는 `pageCount` — 낱장 배치가 확정되면
 * 자연높이를 다시 잡는 ResizeObserver 백업).
 */
import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * fit scale = **실측한 컨테이너 폭** / 페이지 폭 (최대 1 — 넓으면 확대하지 않는다).
 *
 * 컨테이너 폭을 아직 못 쟀으면(0) 1이다(첫 프레임은 useLayoutEffect가 페인트 전에 바로잡는다).
 */
export function calcFitScale(containerWidth: number, pageWidthPx: number): number {
  if (containerWidth <= 0 || pageWidthPx <= 0) return 1;
  return Math.min(1, containerWidth / pageWidthPx);
}

export interface FitWidthMeasure {
  /** 가용 폭을 재는 노드에 단다(호출자가 소유한 바깥 노드 — clientWidth). */
  measureRef: RefObject<HTMLDivElement>;
  /** transform이 걸리는 안쪽 노드에 단다(자연높이 — offsetHeight). */
  innerRef: RefObject<HTMLDivElement>;
  /** 실측 컨테이너 폭 / 낱장 폭, 최대 1. 폭 미측정이면 1. */
  fitScale: number;
  /** 안쪽 노드의 자연높이(px). 아직 못 쟀으면 null. */
  naturalHeight: number | null;
}

export function useFitWidthMeasure(
  pageWidthPx: number,
  enabled = true,
  remeasureKey?: unknown,
): FitWidthMeasure {
  const measureRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const fitScale = calcFitScale(containerWidth, pageWidthPx);

  // 폭 실측 — 첫 페인트 전(useLayoutEffect)에 잡아 잘린 한 프레임을 없앤다. 리사이즈는 ResizeObserver.
  useLayoutEffect(() => {
    if (!enabled) return;
    const node = measureRef.current;
    if (!node) return;
    const read = () => {
      const w = node.clientWidth;
      setContainerWidth((prev) => (prev === w ? prev : w));
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(node);
    return () => ro.disconnect();
  }, [enabled]);

  // wrapper-shrink용 자연높이(offsetHeight — transform 비의존이라 scale과 피드백 루프가 없다).
  const innerRef = useRef<HTMLDivElement>(null);
  const [naturalHeight, setNaturalHeight] = useState<number | null>(null);
  const measureNaturalHeight = useCallback(() => {
    const node = innerRef.current;
    if (!node) return;
    setNaturalHeight((prev) => (prev === node.offsetHeight ? prev : node.offsetHeight));
  }, []);
  useLayoutEffect(() => {
    if (!enabled) return;
    measureNaturalHeight();
    const node = innerRef.current;
    if (!node) return;
    const ro = new ResizeObserver(() => measureNaturalHeight());
    ro.observe(node);
    return () => ro.disconnect();
    // remeasureKey: 호출자가 지정한 재실측 키(낱장 수 등)가 바뀌면 자연높이를 다시 잡는다.
  }, [enabled, measureNaturalHeight, remeasureKey]);

  return { measureRef, innerRef, fitScale, naturalHeight };
}

export default useFitWidthMeasure;
